import React, { useEffect, useState } from 'react';
import { Save, PlusCircle, Trash2, Eye, Upload, X } from 'lucide-react';
import Axios from '../../../services/AxiosInterceptor';
import { useParams } from 'react-router-dom';
import { DatePicker, TimePicker } from 'antd';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);

const STORAGE_URL = "https://yogamatic.in/api/upload/";

  export default function AdminEventPage() {
  const { id } = useParams(); 
  const [event, setEvent] = useState(null); 
  const [previewOpen, setPreviewOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  // File states
  const [heroImageFile, setHeroImageFile] = useState(null);
  const [heroImagePreview, setHeroImagePreview] = useState('');
  const [speakerImageFiles, setSpeakerImageFiles] = useState({});
  const [speakerImagePreviews, setSpeakerImagePreviews] = useState({});
  const [galleryImageFiles, setGalleryImageFiles] = useState([]);
  const [galleryImagePreviews, setGalleryImagePreviews] = useState([]);

  useEffect(() => {
    if (!id) {
      setEvent({
        title: '',
        subtitle: '',
        date: '',
        time: '',
        venue: '',
        heroImage: '',
        about: '',
        tags: [],
        speakers: [],
        schedule: [],
        gallery: [],
        tickets: [],
        contact: {},
        policies: '',
        published: false,
        publishedAt: null,
      });
      return;
    }

    setLoading(true);
    Axios.get(`/api/events/${id}`)
      .then((res) => {
        if (res.data) {
          const ev = { ...res.data };
          if (ev.date) {
            ev.date = ev.date.slice(0, 10);
          }
          setEvent(ev);
        }
      })
      .catch((err) => {
        console.error('Fetch event failed', err);
        setMessage(`Failed to load event: ${err.response?.data?.message || err.message}`);
        setTimeout(() => setMessage(''), 4000);
      })
      .finally(() => setLoading(false));
  }, [id]);


  console.log("dattaaaaa",event)
  function update(path, value) {
    setEvent((prev) => {
      if (!prev) return prev;
      const copy = JSON.parse(JSON.stringify(prev));
      const parts = path.split('.');
      let cur = copy;
      for (let i = 0; i < parts.length - 1; i++) {
        const p = parts[i];
        if (!(p in cur)) cur[p] = {};
        cur = cur[p];
      }
      cur[parts[parts.length - 1]] = value;
      return copy;
    });
  }

  function addItem(listKey, item) {
    setEvent((prev) => ({ ...prev, [listKey]: [...(prev[listKey] || []), item] }));
  }

  function removeItem(listKey, idx) {
    setEvent((prev) => ({ ...prev, [listKey]: prev[listKey].filter((_, i) => i !== idx) }));
    
    // Also remove associated file/preview if it's a speaker
    if (listKey === 'speakers') {
      setSpeakerImageFiles(prev => {
        const newFiles = { ...prev };
        delete newFiles[idx];
        return newFiles;
      });
      setSpeakerImagePreviews(prev => {
        const newPreviews = { ...prev };
        delete newPreviews[idx];
        return newPreviews;
      });
    }
  }

  function updateItem(listKey, idx, field, value) {
    setEvent((prev) => {
      const copy = { ...prev };
      if (field === null) {
        copy[listKey][idx] = value;
      } else {
        copy[listKey] = copy[listKey].map((it, i) => (i === idx ? { ...it, [field]: value } : it));
      }
      return copy;
    });
  }

  // Handle hero image file selection
  function handleHeroImageChange(e) {
    const file = e.target.files[0];
    if (file) {
      setHeroImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setHeroImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  }

  // Handle speaker image file selection
  function handleSpeakerImageChange(idx, e) {
    const file = e.target.files[0];
    if (file) {
      setSpeakerImageFiles(prev => ({ ...prev, [idx]: file }));
      const reader = new FileReader();
      reader.onloadend = () => {
        setSpeakerImagePreviews(prev => ({ ...prev, [idx]: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  }

  // Handle gallery images file selection
  function handleGalleryImagesChange(e) {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      setGalleryImageFiles(files);
      
      // Create previews
      const previews = [];
      files.forEach(file => {
        const reader = new FileReader();
        reader.onloadend = () => {
          previews.push(reader.result);
          if (previews.length === files.length) {
            setGalleryImagePreviews(previews);
          }
        };
        reader.readAsDataURL(file);
      });
    }
  }

  // Helper to get image URL for display
  function getImageUrl(key) {
    if (!key) return '';
    if (key.startsWith('http')) return key;
    return `${STORAGE_URL}${key}`;
  }

  async function saveEvent() {
    if (!event.title || event.title.trim() === '') {
      setMessage('Title is required');
      setTimeout(() => setMessage(''), 2500);
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      
      // Add basic fields
      formData.append('title', event.title || 'Untitled event');
      formData.append('subtitle', event.subtitle || '');
      formData.append('date', event.date || '');
      formData.append('time', event.time || '');
      formData.append('venue', event.venue || '');
      formData.append('about', event.about || '');
      formData.append('policies', event.policies || '');
      formData.append('published', event.published || false);
      
      // Add hero image file or existing URL
      if (heroImageFile) {
        formData.append('heroImage', heroImageFile);
      } else if (event.heroImage) {
        formData.append('heroImage', event.heroImage);
      }
      
      // Add arrays as JSON strings
      formData.append('tags', JSON.stringify(event.tags || []));
      formData.append('schedule', JSON.stringify(event.schedule || []));
      formData.append('tickets', JSON.stringify(event.tickets || []));
      formData.append('contact', JSON.stringify(event.contact || {}));
      
      // Add speakers with their data
      const speakersData = (event.speakers || []).map((speaker, idx) => ({
        name: speaker.name || '',
        title: speaker.title || '',
        image: speaker.image || ''
      }));
      formData.append('speakers', JSON.stringify(speakersData));
      
      // Add speaker image files and indices
      Object.keys(speakerImageFiles).forEach(idx => {
        formData.append('speakerImages', speakerImageFiles[idx]);
        formData.append('speakerImageIndices', idx);
      });
      
      // Add gallery image files
      if (galleryImageFiles.length > 0) {
        galleryImageFiles.forEach(file => {
          formData.append('galleryImages', file);
        });
      }
      // Always send existing gallery
      if (event.gallery && event.gallery.length > 0) {
        formData.append('gallery', JSON.stringify(event.gallery));
      }

      let res;
      if (event._id) {
        res = await Axios.put(`/api/admin/events/${event._id}`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        setMessage('Event updated successfully');
      } else {
        res = await Axios.post('/api/admin/events', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        setMessage('Event created successfully');
      }

      setEvent(res.data);
      
      // Clear file states
      setHeroImageFile(null);
      setHeroImagePreview('');
      setSpeakerImageFiles({});
      setSpeakerImagePreviews({});
      setGalleryImageFiles([]);
      setGalleryImagePreviews([]);
      
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      console.error('Save error', err);
      setMessage(`Save failed: ${err.response?.data?.message || err.message}`);
      setTimeout(() => setMessage(''), 5000);
    } finally {
      setLoading(false);
    }
  }

  if (!event) {
    return (
      <div className="flex items-center justify-center min-h-screen text-gray-500">
        {loading ? 'Loading event...' : 'No event data'}
      </div>
    );
  }

  console.log("heroimage",heroImagePreview)

  return (
    <div className="min-h-screen p-6 bg-gray-50">
      <div className="grid max-w-6xl gap-6 mx-auto lg:grid-cols-3">
        {/* Main form */}
        <div className="space-y-4 lg:col-span-2">
          {/* Header */}
          <div className="flex items-center justify-between gap-4">
            <h1 className="text-2xl font-bold">Edit Event</h1>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPreviewOpen((s) => !s)}
                className="inline-flex items-center gap-2 px-3 py-2 border rounded-md"
              >
                <Eye size={16} /> Preview
              </button>
              <button
                onClick={saveEvent}
                className={`inline-flex items-center gap-2 px-3 py-2 text-white rounded-md ${
                  loading ? 'bg-gray-400' : 'bg-indigo-600'
                }`}
                disabled={loading}
              >
                <Save size={16} /> {loading ? 'Saving...' : 'Update'}
              </button>
            </div>
          </div>

          {/* Basic info */}
          <section className="p-6 bg-white shadow rounded-2xl">
            <h2 className="font-semibold">Basic info</h2>
            <div className="grid gap-3 mt-4">
              <input
                value={event.title}
                onChange={(e) => update('title', e.target.value)}
                className="w-full px-3 py-2 border rounded-md"
                placeholder="Event title"
              />
              <input
                value={event.subtitle}
                onChange={(e) => update('subtitle', e.target.value)}
                className="w-full px-3 py-2 border rounded-md"
                placeholder="Subtitle"
              />
              <div className="grid gap-3 sm:grid-cols-3">
                <DatePicker 
                  value={event.date ? dayjs(event.date) : null} 
                  onChange={(date, dateString) => update('date', dateString)} 
                  className="w-full px-3 py-2 border rounded-md" 
                />
                <TimePicker.RangePicker 
                  value={
                    event.time && event.time.includes(' - ')
                      ? [dayjs(event.time.split(' - ')[0], 'HH:mm'), dayjs(event.time.split(' - ')[1], 'HH:mm')]
                      : null
                  }
                  format="HH:mm"
                  onChange={(times, timeStrings) => {
                    if (times) {
                      update('time', `${timeStrings[0]} - ${timeStrings[1]}`);
                    } else {
                      update('time', '');
                    }
                  }}
                  className="w-full px-3 py-2 border rounded-md" 
                />
                <input
                  value={event.venue}
                  onChange={(e) => update('venue', e.target.value)}
                  className="px-3 py-2 border rounded-md"
                  placeholder="Venue"
                />
              </div>
              
              {/* Hero Image Upload */}
              <div>
                <label className="block mb-2 text-sm font-medium text-gray-700">Hero Image</label>
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-2 px-4 py-2 text-white bg-indigo-600 rounded-md cursor-pointer hover:bg-indigo-700">
                    <Upload size={16} />
                    Choose Image
                    <input type="file" accept="image/*" onChange={handleHeroImageChange} className="hidden" />
                  </label>
                  {(heroImagePreview || event.heroImage) && (
                    <div className="relative">
                      <img 
                        src={heroImagePreview || getImageUrl(event.heroImage)} 
                        alt="Hero preview" 
                        className="object-cover w-20 h-20 border rounded-md" 
                      />
                      {heroImagePreview && (
                        <button 
                          onClick={() => {
                            setHeroImageFile(null);
                            setHeroImagePreview('');
                          }}
                          className="absolute top-0 right-0 p-1 -mt-2 -mr-2 text-white bg-red-500 rounded-full"
                        >
                          <X size={12} />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
              
              <textarea
                value={event.about || ''}
                onChange={(e) => update('about', e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border rounded-md"
                placeholder="About the event"
              />
              <div>
                <label className="text-sm text-gray-600">Tags (comma separated)</label>
                <input
                  value={(event.tags || []).join(', ')}
                  onChange={(e) =>
                    update(
                      'tags',
                      e.target.value.split(',').map((t) => t.trim()).filter(Boolean)
                    )
                  }
                  className="w-full px-3 py-2 border rounded-md"
                />
              </div>
            </div>
          </section>
          
          {/* Speakers */}
          <section className="p-6 bg-white shadow rounded-2xl">
            <h2 className="font-semibold">Speakers</h2>
            <div className="mt-4 space-y-3">
              {(event.speakers || []).map((s, i) => (
                <div key={i} className="p-4 border rounded-lg">
                  <div className="grid items-start gap-3 sm:grid-cols-2">
                    <input value={s.name} onChange={(e) => updateItem('speakers', i, 'name', e.target.value)} className="w-full px-3 py-2 border rounded-md" placeholder="Name" />
                    <input value={s.title} onChange={(e) => updateItem('speakers', i, 'title', e.target.value)} className="w-full px-3 py-2 border rounded-md" placeholder="Title / organization" />
                  </div>
                  
                  <div className="flex items-center gap-3 mt-3">
                    <label className="flex items-center gap-2 px-3 py-2 text-sm text-white bg-indigo-600 rounded-md cursor-pointer hover:bg-indigo-700">
                      <Upload size={14} />
                      Speaker Image
                      <input type="file" accept="image/*" onChange={(e) => handleSpeakerImageChange(i, e)} className="hidden" />
                    </label>
                    
                    {(speakerImagePreviews[i] || s.image) && (
                      <div className="relative">
                        <img 
                          src={speakerImagePreviews[i] || getImageUrl(s.image)} 
                          alt="Speaker preview" 
                          className="object-cover w-16 h-16 border rounded-md" 
                        />
                        {speakerImagePreviews[i] && (
                          <button 
                            onClick={() => {
                              setSpeakerImageFiles(prev => {
                                const newFiles = { ...prev };
                                delete newFiles[i];
                                return newFiles;
                              });
                              setSpeakerImagePreviews(prev => {
                                const newPreviews = { ...prev };
                                delete newPreviews[i];
                                return newPreviews;
                              });
                            }}
                            className="absolute top-0 right-0 p-1 -mt-2 -mr-2 text-white bg-red-500 rounded-full"
                          >
                            <X size={10} />
                          </button>
                        )}
                      </div>
                    )}
                    
                    <button type="button" onClick={() => removeItem('speakers', i)} className="px-3 py-2 ml-auto text-red-600 border rounded-md">
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}

              <div className="mt-2">
                <button onClick={() => addItem('speakers', { name: '', title: '', image: '' })} className="inline-flex items-center gap-2 px-3 py-2 border rounded-md">
                  <PlusCircle size={16} /> Add speaker
                </button>
              </div>
            </div>
          </section>
          
          {/* Schedule */}
          <section className="p-6 bg-white shadow rounded-2xl">
            <h2 className="font-semibold">Schedule</h2>
            <div className="mt-4 space-y-3">
              {(event.schedule || []).map((s, i) => (
                <div key={i} className="grid items-center gap-3 sm:grid-cols-12">
                  <div className="sm:col-span-3">
                    <input value={s.time} onChange={(e) => updateItem('schedule', i, 'time', e.target.value)} className="w-full px-3 py-2 border rounded-md" placeholder="09:00" />
                  </div>
                  <div className="sm:col-span-6">
                    <input value={s.title} onChange={(e) => updateItem('schedule', i, 'title', e.target.value)} className="w-full px-3 py-2 border rounded-md" placeholder="Title" />
                  </div>
                  <div className="sm:col-span-2">
                    <input value={s.location} onChange={(e) => updateItem('schedule', i, 'location', e.target.value)} className="w-full px-3 py-2 border rounded-md" placeholder="Location" />
                  </div>
                  <div className="flex gap-2 sm:col-span-1">
                    <button type="button" onClick={() => removeItem('schedule', i)} className="px-3 py-2 text-red-600 border rounded-md">
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}

              <button onClick={() => addItem('schedule', { time: '', title: '', location: '' })} className="inline-flex items-center gap-2 px-3 py-2 border rounded-md">
                <PlusCircle size={16} /> Add entry
              </button>
            </div>
          </section>
          
          {/* Gallery */}
          <section className="p-6 bg-white shadow rounded-2xl">
            <h2 className="font-semibold">Gallery</h2>
            <p className="text-sm text-gray-500">Upload images for gallery.</p>
            <div className="mt-4 space-y-3">
              <div>
                <label className="flex items-center gap-2 px-4 py-2 text-white bg-indigo-600 rounded-md cursor-pointer hover:bg-indigo-700 w-fit">
                  <Upload size={16} />
                  Choose Gallery Images
                  <input type="file" accept="image/*" multiple onChange={handleGalleryImagesChange} className="hidden" />
                </label>
              </div>
              
              {galleryImagePreviews.length > 0 && (
                <div className="grid grid-cols-4 gap-2">
                  {galleryImagePreviews.map((preview, idx) => (
                    <div key={idx} className="relative">
                      <img src={preview} alt={`Gallery ${idx}`} className="object-cover w-full h-24 border rounded-md" />
                    </div>
                  ))}
                </div>
              )}
              
              {!galleryImagePreviews.length && event.gallery && event.gallery.length > 0 && (
                <div className="grid grid-cols-4 gap-2">
                  {event.gallery.map((img, idx) => (
                    <div key={idx} className="relative">
                      <img src={getImageUrl(img)} alt={`Gallery ${idx}`} className="object-cover w-full h-24 border rounded-md" />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>

          {/* Tickets */}
          <section className="p-6 bg-white shadow rounded-2xl">
            <h2 className="font-semibold">Tickets & Pricing</h2>
            <div className="mt-4 space-y-3">
              {(event.tickets || []).map((t, i) => (
                <div key={i} className="p-4 border rounded-lg bg-gray-50">
                  <div className="grid items-start gap-4 sm:grid-cols-12">
                    <div className="sm:col-span-5">
                      <label className="block mb-1 text-xs font-medium text-gray-500 uppercase">Tier Name</label>
                      <input 
                        value={t.tier} 
                        onChange={(e) => updateItem('tickets', i, 'tier', e.target.value)} 
                        className="w-full px-3 py-2 border rounded-md" 
                        placeholder="e.g. VIP, General Admission" 
                      />
                    </div>
                    <div className="sm:col-span-3">
                      <label className="block mb-1 text-xs font-medium text-gray-500 uppercase">Price</label>
                      <input 
                        type="number" 
                        value={t.price} 
                        onChange={(e) => updateItem('tickets', i, 'price', Number(e.target.value))} 
                        className="w-full px-3 py-2 border rounded-md" 
                        placeholder="0.00" 
                      />
                    </div>
                    <div className="sm:col-span-3">
                      <label className="block mb-1 text-xs font-medium text-gray-500 uppercase">Quantity</label>
                      <input 
                        type="number" 
                        value={t.quantity} 
                        onChange={(e) => updateItem('tickets', i, 'quantity', Number(e.target.value))} 
                        className="w-full px-3 py-2 border rounded-md" 
                        placeholder="Available seats" 
                      />
                    </div>
                    <div className="flex items-end justify-end h-full pb-1 sm:col-span-1">
                      <button 
                        onClick={() => removeItem('tickets', i)} 
                        className="p-2 text-red-600 transition-colors rounded-md hover:bg-red-50"
                        title="Remove ticket tier"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}

              <button onClick={() => addItem('tickets', { tier: '', price: 0, quantity: 0 })} className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-indigo-600 transition-colors border border-indigo-200 rounded-md hover:bg-indigo-50">
                <PlusCircle size={16} /> Add Ticket Tier
              </button>
            </div>
          </section>

          {/* Contact & Policies */}
          <section className="p-6 bg-white shadow rounded-2xl">
            <h2 className="font-semibold">Contact & Policies</h2>
            <div className="grid gap-3 mt-4">
              <input value={event.contact.email} onChange={(e) => update('contact.email', e.target.value)} className="px-3 py-2 border rounded-md" placeholder="Contact email" />
              <input value={event.contact.phone} onChange={(e) => update('contact.phone', e.target.value)} className="px-3 py-2 border rounded-md" placeholder="Phone" />
              <textarea value={event.policies || ''} onChange={(e) => update('policies', e.target.value)} rows={3} className="px-3 py-2 border rounded-md" placeholder="Refund, privacy, accessibility notes" />
            </div>
          </section>
        </div>

        {/* Sidebar */}
         <aside className="space-y-4">
        {/* <div className="sticky p-4 bg-white shadow rounded-2xl top-6">
          <h3 className="font-semibold">Quick actions</h3>
          <div className="flex flex-col gap-2 mt-3">
            <button onClick={() => {
              const copy = JSON.stringify(event, null, 2);
              navigator.clipboard.writeText(copy);
              setMessage('JSON copied to clipboard');
              setTimeout(() => setMessage(''), 2000);
            }} className="px-3 py-2 border rounded-md">Copy JSON</button>

            <button onClick={async () => {
              const published = localStorage.getItem('admin_event_published');
              if (!published) return alert('Nothing published yet');
              const parsed = JSON.parse(published);
              setEvent(parsed);
              setMessage('Loaded published event');
              setTimeout(() => setMessage(''), 2000);
            }} className="px-3 py-2 border rounded-md">Load published</button>

            <button onClick={() => { localStorage.removeItem('admin_event_draft'); ; setMessage('Draft cleared'); setTimeout(() => setMessage(''), 1500); }} className="px-3 py-2 text-red-600 border rounded-md">Clear draft</button>
          </div>
        </div> */}

        <div className="p-4 bg-white shadow rounded-2xl">
          <h3 className="font-semibold">Preview</h3>
          <div className="mt-3 text-sm text-gray-700">
            <div className="font-medium">{event.title}</div>
            <div className="text-xs text-gray-500">{event.date} • {event.time}</div>
            <div className="mt-2 text-sm">{event.about?.slice(0, 140)}{event.about && event.about.length > 140 ? '…' : ''}</div>
            <div className="flex gap-2 mt-3">
              <button onClick={() => setPreviewOpen(true)} className="px-3 py-2 border rounded-md">Open big preview</button>
              <button onClick={() => { navigator.clipboard.writeText(window.location.href); setMessage('URL copied'); setTimeout(() => setMessage(''), 1200); }} className="px-3 py-2 border rounded-md">Copy URL</button>
            </div>
          </div>
        </div>

        <div className="p-4 bg-white shadow rounded-2xl">
          <h3 className="font-semibold">Published status</h3>
          <div className="mt-2 text-sm text-gray-700">Last published: <span className="font-medium">{localStorage.getItem('admin_event_published') ? 'Yes (localStorage)' : 'Not yet'}</span></div>
        </div>
      </aside>
      </div>

      {/* Preview modal */}
      {previewOpen && (
        <div className="fixed inset-0 z-50 flex items-start justify-center p-6 bg-black/60">
          <div className="w-full max-w-4xl rounded-2xl bg-white p-6 overflow-auto max-h-[90vh]">
            <div className="flex items-start justify-between">
              <h2 className="text-lg font-semibold">Live preview</h2>
              <button onClick={() => setPreviewOpen(false)} className="px-3 py-2 border rounded-md">Close</button>
            </div>

            <div className="mt-4">
              <img src={heroImagePreview || getImageUrl(event.heroImage) || 'https://images.unsplash.com/photo-1521737604893-d14cc237f11d?q=80&w=1600&auto=format&fit=crop'} alt="hero" className="object-cover w-full h-48 rounded-lg" />
              <h1 className="mt-4 text-2xl font-bold">{event.title}</h1>
              <p className="text-sm text-gray-500">{event.subtitle}</p>
              <div className="mt-2 text-gray-700">{event.about}</div>

              <div className="grid gap-4 mt-4 sm:grid-cols-3">
                <div className="p-3 border rounded-lg">
                  <div className="text-xs text-gray-500">When</div>
                  <div className="font-medium">{event.date} • {event.time}</div>
                </div>
                <div className="p-3 border rounded-lg">
                  <div className="text-xs text-gray-500">Where</div>
                  <div className="font-medium">{event.venue}</div>
                </div>
                <div className="p-3 border rounded-lg">
                  <div className="text-xs text-gray-500">Contact</div>
                  <div className="font-medium">{event.contact.email}</div>
                </div>
              </div>

              <div className="mt-6">
                <h3 className="font-semibold">Tickets</h3>
                <div className="grid gap-3 mt-2 sm:grid-cols-2">
                  {(event.tickets || []).map((t, i) => (
                    <div key={i} className="p-3 border rounded-lg">
                      <div className="font-medium">{t.tier}</div>
                      <div className="text-sm text-gray-600">Price: ${t.price} — Qty: {t.quantity}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-6">
                <h3 className="font-semibold">Speakers</h3>
                <div className="grid gap-3 mt-2 sm:grid-cols-2">
                  {(event.speakers || []).map((s, i) => (
                    <div key={i} className="flex items-center gap-3 p-3 border rounded-lg">
                      <div className="w-12 h-12 overflow-hidden bg-gray-100 rounded-full">
                        {(speakerImagePreviews[i] || s.image) ? <img src={speakerImagePreviews[i] || getImageUrl(s.image)} alt={s.name} className="object-cover w-full h-full" /> : null}
                      </div>
                      <div>
                        <div className="font-medium">{s.name}</div>
                        <div className="text-sm text-gray-600">{s.title}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {(galleryImagePreviews.length > 0 || (event.gallery && event.gallery.length > 0)) && (
                <div className="mt-6">
                  <h3 className="font-semibold">Gallery</h3>
                  <div className="grid grid-cols-4 gap-2 mt-2">
                    {galleryImagePreviews.length > 0 
                      ? galleryImagePreviews.map((img, i) => <img key={i} src={img} alt="" className="object-cover w-full h-24 rounded-md" />)
                      : (event.gallery || []).map((img, i) => <img key={i} src={getImageUrl(img)} alt="" className="object-cover w-full h-24 rounded-md" />)
                    }
                  </div>
                </div>
              )}

            </div>
          </div>
        </div>
      )}

      {/* Message */}
      {message && (
        <div className="fixed px-4 py-2 text-white transform -translate-x-1/2 bg-indigo-600 rounded-md bottom-4 left-1/2">
          {message}
        </div>
      )}
    </div>
  );
}
